import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Save } from 'lucide-react';

const templateLabels = {
  order_internal: { title: 'Interne Bestellmail (an GB Sylt)', desc: 'Diese Mail wird bei jeder neuen Bestellung an info@gb-sylt.de gesendet. Platzhalter: {{lieferanten_freigabe}} für den Freigabe-Button.' },
  order_confirmation: { title: 'Bestellbestätigung (an Gast)', desc: 'Diese Mail erhält der Gast nach der Bestellung.' },
  reminder: { title: 'Erinnerungsmail (intern)', desc: 'Automatische Erinnerung 2 Tage vor Anreise.' },
  supplier_order: { title: 'Lieferanten-Bestellmail', desc: 'Diese Mail wird an Lieferanten gesendet. Platzhalter: {{anrede}}, {{lieferant_firma}}, {{lieferant_name}}, {{bestellnummer}}, {{anreisedatum}}, {{produktliste}}' },
};

const placeholders = ['{{vorname}}', '{{nachname}}', '{{email}}', '{{telefon}}', '{{nachricht}}', '{{buchungsnummer}}', '{{bestellnummer}}', '{{vorgangsnummer}}', '{{bestelldatum}}', '{{anreisedatum}}', '{{produktübersicht}}', '{{produktsumme}}', '{{servicepauschale}}', '{{gesamtsumme}}', '{{bankverbindung}}', '{{lieferanten_freigabe}}'];

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get('/mail-templates').then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const updateField = (key, field, value) => {
    setTemplates(prev => prev.map(t => t.template_key === key ? { ...t, [field]: value } : t));
  };

  const handleSave = async (key) => {
    const tmpl = templates.find(t => t.template_key === key);
    if (!tmpl) return;
    try {
      await api.put(`/mail-templates/${key}`, { subject: tmpl.subject, body_html: tmpl.body_html });
      toast.success('Vorlage gespeichert');
    } catch (e) { toast.error(`Fehler: ${e.response?.data?.detail || e.message}`); }
  };

  return (
    <div data-testid="admin-email-templates">
      <h1 className="text-2xl font-bold text-[#0E518D] mb-6">E-Mail Vorlagen</h1>

      <div className="mb-6 bg-white rounded-sm shadow-sm border border-slate-100 p-4">
        <p className="text-sm text-[#475569] mb-2">Verfügbare Platzhalter:</p>
        <div className="flex flex-wrap gap-2">
          {placeholders.map(p => (
            <Badge key={p} variant="outline" className="text-xs font-mono border-[#0E518D] text-[#0E518D]">{p}</Badge>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3 italic">{"{{bankverbindung}}"} fuegt automatisch Kontoinhaber, Bank, IBAN, BIC und den Verwendungszweck (Vorgangsnummer + Buchungsnummer) ein. Bankdaten werden unter Einstellungen gepflegt.</p>
      </div>

      <div className="space-y-6">
        {templates.map(t => {
          const info = templateLabels[t.template_key] || { title: t.template_key, desc: '' };
          return (
            <Card key={t.template_key} className="bg-white rounded-sm shadow-sm border border-slate-100" data-testid={`template-${t.template_key}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-[#0E518D]">{info.title}</CardTitle>
                    <p className="text-xs text-slate-400 mt-1">{info.desc}</p>
                  </div>
                  <Button onClick={() => handleSave(t.template_key)} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid={`save-template-${t.template_key}`}>
                    <Save className="h-3.5 w-3.5 mr-1" /> Speichern
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm">Betreffzeile</Label>
                  <Input value={t.subject || ''} onChange={e => updateField(t.template_key, 'subject', e.target.value)} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-sm">Inhalt (HTML)</Label>
                  <Textarea value={t.body_html || ''} onChange={e => updateField(t.template_key, 'body_html', e.target.value)} rows={10} className="mt-1 rounded-sm font-mono text-xs" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
